/**
 * User System - Role definitions, permissions, and sample users
 *
 * Designed for easy migration to Supabase auth later.
 * All downstream code keys off currentUser.id and currentUser.role.
 */

// ─── ROLE DEFINITIONS ───
export const ROLES = {
  reception: {
    label: 'Reception',
    colour: '#3B82F6', // blue
    description: 'Day-to-day operations',
  },
  manager: {
    label: 'Manager',
    colour: '#F59E0B', // amber
    description: 'Full oversight & email management',
  },
  admin: {
    label: 'Admin',
    colour: '#8B5CF6', // purple
    description: 'Full access & user management',
  },
  housekeeping: {
    label: 'Housekeeping',
    colour: '#B8963E', // gold
    description: 'Shared phone / iPad login – housekeeping screen only',
  },
};

// ─── PAGE ACCESS BY ROLE ───
// true = full access, 'limited' = partial (e.g. billing without revenue)
export const PAGE_ACCESS = {
  reception: {
    my_dashboard: true,
    pipeline: true,
    room_diary: true,
    events: true,
    tables: true,
    function_sheets: true,
    housekeeping: true,
    billing: 'limited',   // can create/view invoices, no revenue totals
    clients: true,
    dashboard: true,       // unified dashboard (revenue KPIs hidden by permissions)
    email: false,          // no Email Centre
    marketing: false,      // no Marketing
    integrations: false,
  },
  manager: {
    my_dashboard: true,
    pipeline: true,
    room_diary: true,
    events: true,
    tables: true,
    function_sheets: true,
    housekeeping: true,
    billing: true,
    clients: true,
    dashboard: true,
    email: true,
    marketing: true,
    integrations: true,
  },
  admin: {
    my_dashboard: true,
    pipeline: true,
    room_diary: true,
    events: true,
    tables: true,
    function_sheets: true,
    housekeeping: true,
    billing: true,
    clients: true,
    dashboard: true,
    email: true,
    marketing: true,
    integrations: true,
    user_management: true,
  },
  housekeeping: {
    housekeeping: true,
  },
};

// ─── ACTION PERMISSIONS ───
export const ACTION_PERMISSIONS = {
  reception: {
    canDeleteEvents: false,
    canDeletePayments: false,
    canEditSettings: false,
    canSendEmails: false,
    canViewRevenue: false,
    canManageUsers: false,
    canViewActivityFeed: false,
  },
  manager: {
    canDeleteEvents: true,
    canDeletePayments: true,
    canEditSettings: true,
    canSendEmails: true,
    canViewRevenue: true,
    canManageUsers: false,
    canViewActivityFeed: false,
  },
  admin: {
    canDeleteEvents: true,
    canDeletePayments: true,
    canEditSettings: true,
    canSendEmails: true,
    canViewRevenue: true,
    canManageUsers: true,
    canViewActivityFeed: true,
  },
  housekeeping: {
    canDeleteEvents: false,
    canDeletePayments: false,
    canEditSettings: false,
    canSendEmails: false,
    canViewRevenue: false,
    canManageUsers: false,
    canViewActivityFeed: false,
  },
};

// ─── SAMPLE USERS (replace with Supabase auth later) ───
export const SAMPLE_USERS = [
  {
    id: 'user_reception_1',
    name: 'Sarah',
    role: 'reception',
    initials: 'S',
  },
  {
    id: 'user_manager_1',
    name: 'Ronan',
    role: 'manager',
    initials: 'R',
  },
  {
    id: 'user_admin_1',
    name: 'Admin',
    role: 'admin',
    initials: 'A',
  },
];

// ─── HELPER FUNCTIONS ───

/** Check if a role can access a specific page */
export const canAccessPage = (role, pageKey) => {
  const access = PAGE_ACCESS[role];
  if (!access) return false;
  return access[pageKey] === true || access[pageKey] === 'limited';
};

/** Check if a role has limited access to a page */
export const hasLimitedAccess = (role, pageKey) => {
  return PAGE_ACCESS[role]?.[pageKey] === 'limited';
};

/** Get action permissions for a role */
export const getPermissions = (role) => {
  return ACTION_PERMISSIONS[role] || ACTION_PERMISSIONS.reception;
};

/** Get user's initials colour based on role */
export const getRoleColour = (role) => {
  return ROLES[role]?.colour || '#6B7280';
};

// ─── NOTIFICATION TYPES ───
export const NOTIFICATION_TYPES = {
  new_enquiry: { label: 'New Enquiry', icon: 'inbox', roles: ['reception', 'manager', 'admin'] },
  stage_change: { label: 'Stage Changed', icon: 'move', roles: ['manager', 'admin'] },
  email_opened: { label: 'Email Opened', icon: 'eye', roles: ['manager', 'admin'] },
  deposit_overdue: { label: 'Deposit Overdue', icon: 'alert', roles: ['reception', 'manager', 'admin'] },
  event_tomorrow: { label: 'Event Tomorrow', icon: 'calendar', roles: ['reception', 'manager', 'admin'] },
  event_today: { label: 'Event Today', icon: 'calendar', roles: ['reception', 'manager', 'admin'] },
  payment_received: { label: 'Payment Received', icon: 'check', roles: ['reception', 'manager', 'admin'] },
  task_assigned: { label: 'Task Assigned', icon: 'clipboard', roles: ['reception', 'manager', 'admin'] },
};

/** Check if a notification type is relevant to a role */
export const isNotificationRelevant = (type, role) => {
  const config = NOTIFICATION_TYPES[type];
  return config?.roles?.includes(role) || false;
};
